import ms from "ms";
import i18n from "../../i18n/config";
import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import orderBy from "lodash/orderBy";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { modalService } from "@whatslly/ui-components/modals/ModalService";
import { deleteJSON, getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import type {
  ApplySnapshotResponse,
  BulkReinviteSidecars,
  CreateSidecar,
  DeleteSidecar,
  ListSidecars,
  ListSidecarSnapshots,
} from "@whatslly/types/sidecars-server/Sidecar";
import {
  channelTypeOptions,
  ClientSideSidecar,
  CreateSidecarResponse,
  ReInviteSidecar,
  SIDECAR_RUNNERS,
  SIDECAR_STATUS,
} from "@whatslly/types/sidecars-server/Sidecar";
import type { SidecarCommandRequest, SidecarCommandResponse, SidecarJobStatus } from "@whatslly/types/sidecars-server/SidecarCommands";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import { commandModals, ErrorModal, SharePairingUrlModal } from "./CommandModals";
import { SelectCommandModal } from "./CommandModals/SelectCommandModal";
import { SelectSnapshotModal } from "./CommandModals/SelectSnapshotModal";
import { CreateSidecarModal } from "./CreateSidecarModal";
import { SidecarSettingsModal } from "./SidecarSettingsModal/SidecarSettingsModal";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import type { TableSort } from "@whatslly/ui-components/v2/Tables/Table/TableTypes";
import { UrlStore } from "../../services/urlStore";
import type { TFunction } from "i18next";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import type { PairingIdentification } from "./types";
import { SidecarPairingStore } from "./SidecarPairingStore";
import { arrayToCsv } from "@whatslly/commons/csvUtils";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";
import dayjs from "dayjs";
import { UserGroupsStore } from "../UserGroups/UserGroupsStore";
import type { SupportedSidecarChannel } from "@whatslly/commons/sidecarUtils";
import { sidecarStatusLabel } from "@whatslly/commons/sidecarUtils";
import { ChannelsPageStore } from "../ChannelsPage/ChannelsPageStore";
import { MixpanelService } from "@whatslly/commons/mixpanel/MixpanelService";
import { OrgConfigsStore } from "../../services/OrgConfigsStore";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { parseOrReturnError } from "../../services/utils";
import type { ISidecarsStore } from "./ISidecarsStore";
const PAIRING_REFRESH_INTERVAL = import.meta.env.MODE === "test" ? ms("100ms") : ms("2.5s");
const COMMAND_TIMEOUT = ms("5m");
export const PAIR_STATUSES = ["connected", "not_paired", "new", "disconnected_by_user"] as const;

type Status = (typeof SIDECAR_STATUS)[keyof typeof SIDECAR_STATUS];
type Runner = (typeof SIDECAR_RUNNERS)[number];

const columnSort = {
  "user.name": "user.name",
  "user.email": "user.email",
  "user.username": "user.username",
  "metadata.email": "metadata.email",
  "metadata.formatted": "metadata.formatted",
  "metadata.pairedDevices": "metadata.pairedDevices",
  "runnerState.type": "runnerState.type",
  displayStatus: (sidecar: ClientSideSidecar) => sidecarStatusLabel(sidecar),
  displayStatusSa: "displayStatus",
  updatedAt: (sidecar: ClientSideSidecar) => new Date(sidecar.updatedAt).valueOf(),
  createdAt: (sidecar: ClientSideSidecar) => new Date(sidecar.createdAt).valueOf(),
  disconnectedAt: (sidecar: ClientSideSidecar) =>
    !sidecar.metadata?.isPaired && sidecar.metadata?.disconnectedAt ? dayjs(sidecar.metadata.disconnectedAt).valueOf() : 0,
} as const;
const invertSort = new Set(["updatedAt"]);

@injectable()
export class SidecarsStore implements ISidecarsStore {
  _sidecars: Array<ClientSideSidecar> = [];
  default_sidecar_frequency = "0 */3 * * *";
  _sort: TableSort = { keyInsideItem: "user.email", direction: "asc" };
  _channel: SupportedSidecarChannel | undefined;
  sidecarsUpdating = false;
  isInitialized = false;
  clearQueryCacheFromPromise?: IPromiseBasedObservable<undefined>;
  selectedItems: Array<ClientSideSidecar> = [];
  bulkDeleteProgress: { current: number; total: number } | null = null;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(UserGroupsStore) private readonly userGroupsStore: UserGroupsStore,
    @inject(ChannelsPageStore) private readonly channelsPageStore: ChannelsPageStore,
    @inject(MixpanelService) private readonly mixpanelService: MixpanelService,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      isInitialized: observable,
      _sidecars: observable.shallow,
      _sort: observable.shallow,
      _channel: observable,
      channel: computed,
      default_sidecar_frequency: observable,
      sidecarsUpdating: observable,
      clearQueryCacheFromPromise: observable,
      selectedItems: observable.shallow,
      bulkDeleteProgress: observable,
      searchTerm: computed,
      statusFilters: computed,
      pairStatusFilters: computed,
      runnerFilter: computed,
      groupsFilter: computed,
      urlSearchParams: computed,
      maxDevicesForChannel: computed,
      availableChannelsForCreation: computed,
      canCreateSidecar: computed,
      sort: computed,
      filteredSidecars: computed,
      updateSidecars: action.bound,
      createSidecar: action.bound,
      setSearchTerm: action,
      toggleStatusFilter: action,
      toggleRunnerFilter: action,
      toggleGroupsFilter: action,
      toggleItemSelection: action.bound,
      deselectAllItems: action.bound,
    });
    reaction(
      () => [this.urlSearchParams, this.channel],
      () => {
        this.isInitialized = false;
        this.sidecarsUpdating = true;
        this.updateSidecars();
      },
      { fireImmediately: true }
    );
  }

  get sort() {
    return this._sort;
  }
  set sort(sort: TableSort) {
    this._sort = sort;
  }

  get urlSearchParams() {
    return fromPromise(this.orgStore.selectedOrg.then((org) => (org ? `?orgId=${org.value}` : "")));
  }

  getMaxDevicesForChannel(channel: SupportedSidecarChannel): number {
    if (this.channelsPageStore.init.state !== "fulfilled") {
      return 0;
    }
    const perChannelValue = this.channelsPageStore.configs[`max_devices_${channel}`] ?? 0;
    // For WhatsApp, fall back to legacy max_sidecars if max_devices_whatsapp is not set (0 is the default)
    if (channel === "whatsapp" && perChannelValue === 0) {
      return this.channelsPageStore.configs.max_sidecars ?? 0;
    }
    return perChannelValue;
  }

  get maxDevicesForChannel(): number {
    if (this.channelsPageStore.init.state !== "fulfilled") {
      return 0;
    }
    if (!this.channel) {
      // Sum max devices across all enabled channels
      return channelTypeOptions
        .filter((opt) => this.channelsPageStore.isSidecarChannelEnabled(opt.value))
        .reduce((sum, opt) => sum + this.getMaxDevicesForChannel(opt.value), 0);
    }
    return this.getMaxDevicesForChannel(this.channel);
  }

  get limitDateRangeOfMessages() {
    return this.orgConfigsStore.getConfig("limit_date_range_of_messages");
  }

  hasAvailableSlotsForChannel(channel: SupportedSidecarChannel): boolean {
    if (this.channelsPageStore.init.state !== "fulfilled") {
      return false;
    }
    const countForChannel = this._sidecars.filter((s) => s.runnerChannel?.type === channel).length;
    return this.getMaxDevicesForChannel(channel) > countForChannel;
  }

  get availableChannelsForCreation(): Array<(typeof channelTypeOptions)[number]> {
    if (this.channelsPageStore.init.state !== "fulfilled") {
      return [];
    }

    return channelTypeOptions.filter((opt) => {
      if (!this.channelsPageStore.isSidecarChannelEnabled(opt.value)) return false;
      return this.hasAvailableSlotsForChannel(opt.value);
    });
  }
  get canCreateSidecar() {
    return this.canCreateSidecarForChannel();
  }

  canCreateSidecarForChannel(channel?: SupportedSidecarChannel): boolean {
    if (!hasPermission(this.userStore.user, UserPermissions["sidecars.create"])) {
      return false;
    }

    if (this.channelsPageStore.init.state !== "fulfilled") {
      return false;
    }

    const requestedChannel = channel ?? this.channel;
    if (requestedChannel) {
      // Keep consistent behavior with `availableChannelsForCreation`: a channel must be enabled and have free slots.
      if (!this.channelsPageStore.isSidecarChannelEnabled(requestedChannel)) {
        return false;
      }
      return this.hasAvailableSlotsForChannel(requestedChannel);
    }
    // No specific channel: allow creation if at least one enabled channel has an available slot.
    return this.availableChannelsForCreation.length > 0;
  }

  get statusFilters(): Set<Status> {
    const status = this.urlStore.searchParams?.get("status") ?? "";
    if (!status) {
      return new Set(Object.values(SIDECAR_STATUS));
    }
    return new Set(status.split(",").filter(Boolean) as Status[]);
  }
  set statusFilters(statuses) {
    if (statuses.size === Object.values(SIDECAR_STATUS).length) {
      this.urlStore.setSearchParams({ status: "" });
      return;
    }
    this.urlStore.setSearchParams({ status: Array.from(statuses).join(",") });
  }

  get pairStatusFilters(): Set<string> {
    const status = this.urlStore.searchParams?.get("pairStatus") ?? "";
    if (!status) {
      return new Set([...PAIR_STATUSES]);
    }

    return new Set(status.split(",").filter(Boolean));
  }

  set pairStatusFilters(statuses) {
    if (statuses.size === PAIR_STATUSES.length) {
      this.urlStore.setSearchParams({ pairStatus: "" });
      return;
    }
    this.urlStore.setSearchParams({ pairStatus: Array.from(statuses).join(",") });
  }

  async downloadCsvFile(t: TFunction) {
    const groupNames = await this.userGroupsStore.groupsList;
    const usersPick = this._sidecars.map((sidecar) => {
      return [
        sidecar.user?.name ?? "",
        sidecar.user?.email ?? "",
        sidecar.user?.username ?? "",
        sidecar.metadata?.phone ?? "",
        sidecar.metadata?.pairedDevices ?? "",
        sidecarStatusLabel(sidecar, t),
        sidecar.user?.groupIds
          ?.map((groupId) => groupNames.find((group) => group._id === groupId)?.name)
          .filter(Boolean)
          .join(", ") ?? "",
        sidecar.user?.manager?.name ?? "",
        sidecar.metadata?.autoReinvite ? "True" : "False",
        sidecar.metadata?.periodicReinvite ? "True" : "False",
        sidecar.metadata?.notifyEmail ? "True" : "False",
      ];
    });

    const csvUsers = arrayToCsv([
      [
        t("sidecars.user"),
        t("email"),
        t("user_management.cols.username"),
        t("sidecars.phone"),
        t("sidecars.paired_devices"),
        t("sidecars.status"),
        t("user_management.cols.groups"),
        t("sidecars.manager"),
        t("sidecars.auto_reinvite"),
        t("sidecars.periodic_reinvite"),
        t("sidecars.should_notify"),
      ],
      ...usersPick,
    ]);

    downloadBlob(csvUsers, `tuvis-mobiles-${dayjs().format("YYYY-MM-DD-HH-mm-ss")}.csv`, "text/csv;charset=utf-8;");
    this.mixpanelService.trackCsvExported({ tableName: "Remote Sync" });
  }

  togglePairStatusFilter(status: string) {
    let newFilter = new Set(this.pairStatusFilters);
    if (newFilter.size === PAIR_STATUSES.length) {
      newFilter = new Set([status]);
    } else if (newFilter.size === 1 && newFilter.has(status)) {
      newFilter = new Set(PAIR_STATUSES);
    } else if (newFilter.has(status)) {
      newFilter.delete(status);
    } else {
      newFilter.add(status);
    }
    this.pairStatusFilters = newFilter;
  }

  get runnerFilter(): Set<Runner> {
    const runner = this.urlStore.searchParams?.get("runner") ?? "";
    if (!runner) {
      return new Set(SIDECAR_RUNNERS);
    }
    return new Set(runner.split(",").filter(Boolean) as Runner[]);
  }
  set runnerFilter(runners) {
    if (runners.size === SIDECAR_RUNNERS.length) {
      this.urlStore.setSearchParams({ runner: "" });
      return;
    }
    this.urlStore.setSearchParams({ runner: Array.from(runners).join(",") });
  }

  get groupsFilter(): Set<string> {
    const groups = this.urlStore.searchParams?.get("groups") ?? "";
    if (!groups) {
      return new Set([]);
    }
    return new Set(groups.split(",").filter(Boolean) as string[]);
  }
  set groupsFilter(groups) {
    this.urlStore.setSearchParams({ groups: Array.from(groups).join(",") });
  }

  get searchTerm() {
    return this.urlStore.searchParams?.get("search") ?? "";
  }

  get filteredSidecars() {
    const term = this.searchTerm.trim().toLowerCase();
    const termNumbers = term.replace(/\D/g, "");
    // TODO: Filter and sort should be done on the server when pagination is implemented
    return orderBy(
      this._sidecars.filter(
        (sidecar) =>
          this.statusFilters.has(sidecar.displayStatus) &&
          this.pairStatusFilters.has(sidecarStatusLabel(sidecar)) &&
          this.runnerFilter.has(sidecar.runnerState.type) &&
          (this.groupsFilter.size === 0 || sidecar.user?.groupIds?.some((group) => this.groupsFilter.has(group)) === true) &&
          ((termNumbers && sidecar.metadata?.formatted?.replace(/\D/g, "").includes(termNumbers)) ||
            !sidecar.user?.name ||
            sidecar.user.name.toLowerCase().includes(term))
      ),
      columnSort[this._sort.keyInsideItem as unknown as keyof typeof columnSort] ?? columnSort["metadata.email"],
      invertSort.has(this._sort.keyInsideItem) ? (this._sort.direction === "desc" ? "asc" : "desc") : this._sort.direction
    );
  }

  async updateSidecars() {
    this.sidecarsUpdating = true;
    const searchParams = new URLSearchParams(await this.urlSearchParams);
    if (this.channel) {
      searchParams.set("channel", this.channel);
    }
    const paramsString = searchParams.toString();
    const [data] = await Promise.all([
      getJSON({
        urlSuffix: `/sidecars/${paramsString ? `?${paramsString}` : ""}`,
        etoken: this.userStore.etoken ?? "",
      })
        .then((res) => res.json() as Promise<ListSidecars>)
        .catch(() => ({ success: false }) as ListSidecars),
    ]);

    runInAction(() => {
      this._sidecars = data.success ? Array.from(data.sidecars) : [];
      this.default_sidecar_frequency = data.success ? data.default_sidecar_frequency : "0 */3 * * *";
      this.sidecarsUpdating = false;
      this.isInitialized = true;
      this.selectedItems = [];
    });
    this.channelsPageStore.loadSidecarsCount();
  }

  async createSidecar(user?: { _id: string }, channel?: SupportedSidecarChannel) {
    if (!this.canCreateSidecarForChannel(channel)) {
      return;
    }
    const filteredChannelOptions = channel
      ? this.availableChannelsForCreation.filter((opt) => opt.value === channel)
      : this.availableChannelsForCreation;
    const channelOptionsToShow = filteredChannelOptions.length ? filteredChannelOptions : this.availableChannelsForCreation;

    const create = await modalService.openModal(CreateSidecarModal, {
      showSettings: true,
      user,
      channelOptions: channelOptionsToShow,
    }).promise;
    if (!create) {
      return;
    }
    const { invites, runnerType, channelType } = create;
    const settings = await modalService.openModal(SidecarSettingsModal).promise;
    if (!settings) {
      return;
    }
    const bodyObj: CreateSidecar = {
      invites,
      settings: {
        runMode: settings.runMode,
        autoReinvite: settings.autoReinvite === "on",
        periodicReinvite: settings.periodicReinvite === "on",
        notifyEmail: settings.notifyEmail === "on",
        locale: settings.locale,
      },
      runnerType,
      channelType,
      inviteUrl: `${location.origin}${import.meta.env.VITE_BASE_URL?.toString() ?? "/"}`,
    };
    const response: CreateSidecarResponse = await putJSON({
      urlSuffix: `/sidecars/${await this.urlSearchParams}`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify(bodyObj),
    }).then((res) => res.json());
    if (!response.success) {
      modalService.openModal(ErrorModal, { error: response.error });
      return;
    }
    if (response.pairingUrl.length === 1) {
      await modalService.openModal(SharePairingUrlModal, {
        pairingUrl: response.pairingUrl[0],
        created: true,
      }).promise;
    }
    await this.updateSidecars();
  }

  async restoreLatestSnapshot(sidecarId: string) {
    const snapshotsRes = await getJSON({
      urlSuffix: `/sidecars/${sidecarId}/snapshots${await this.urlSearchParams}`,
      etoken: this.userStore.etoken ?? "",
    }).then((r) => parseOrReturnError<ListSidecarSnapshots>(r, this.logger));

    if (!snapshotsRes.success) {
      modalService.openModal(ErrorModal, { error: snapshotsRes.error });
      return;
    }
    const snapshots = snapshotsRes.snapshots;
    if (!snapshots || snapshots.length === 0) {
      modalService.openModal(Toast, { text: "No snapshots found", type: "error" });
      return;
    }
    // If only 1 snapshot, use it. Otherwise, allow selection
    let chosen = snapshots[0];
    if (snapshots.length > 1) {
      chosen = await modalService.openModal(SelectSnapshotModal, { snapshots } as any).promise;
    }
    const confirm = await modalService.openModal(ConfirmModal, {
      title: "Restore EBS volume from snapshot",
      body: `Snapshot: ${chosen.name}\nCreated: ${new Date(chosen.createdAt).toLocaleString()}`,
    }).promise;
    if (!confirm?.result) return;

    const applyRes = await postJSON({
      urlSuffix: `/sidecars/${sidecarId}/apply-snapshot${await this.urlSearchParams}`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({ snapshotName: chosen.name }),
    }).then((r) => parseOrReturnError<ApplySnapshotResponse>(r, this.logger));
    if (!applyRes.success) {
      modalService.openModal(ErrorModal, { error: applyRes.error });
      return;
    }
    modalService.openModal(Toast, { text: "Snapshot applied. Reboot sidecar to use restored data.", type: "regular" });
    this.updateSidecars();
  }

  private async enqueueCommand(sidecarId: string, identification: PairingIdentification, command: SidecarCommandRequest) {
    const { searchParamsString, etokenSpread } = SidecarPairingStore.identify(identification);
    const responce: SidecarCommandResponse = await postJSON({
      urlSuffix: `/sidecars/${sidecarId}/command${searchParamsString}`,
      body: JSON.stringify(command),
      ...etokenSpread,
    }).then((res) => res.json());
    return responce;
  }

  async reinviteSidecar(sidecarId: string): Promise<void> {
    const response: ReInviteSidecar = await getJSON({
      urlSuffix: `/sidecars/${sidecarId}/re-invite${await this.urlSearchParams}`,
      etoken: this.userStore.etoken ?? "",
    }).then((res) => res.json());
    if (!response.success) {
      modalService.openModal(ErrorModal, { error: response.error });
      return;
    }
    await modalService.openModal(SharePairingUrlModal, response).promise;
  }

  private awaitJob(sidecarId: string, jobId: string, identification: PairingIdentification, signal?: AbortSignal) {
    return new Promise<(SidecarJobStatus & { success: true })["result"]>((resolve, reject) => {
      const startedAt = Date.now();
      const { searchParamsString, etokenSpread } = SidecarPairingStore.identify(identification);
      const interval = setInterval(async () => {
        if (signal?.aborted) {
          reject(signal.reason);
          clearInterval(interval);
          return;
        }
        const job = await getJSON({
          urlSuffix: `/sidecars/${sidecarId}/job/${jobId}${searchParamsString}`,
          ...etokenSpread,
          signal,
        }).then((res) => res.json() as Promise<SidecarJobStatus>);
        if (signal?.aborted) {
          reject(signal.reason);
          clearInterval(interval);
        }
        if (job.success) {
          clearInterval(interval);
          resolve(job.result);
          return;
        }
        if (Date.now() - startedAt > COMMAND_TIMEOUT) {
          reject(new Error("Command timed out"));
          clearInterval(interval);
        }
      }, PAIRING_REFRESH_INTERVAL);
    });
  }

  async runCommand(
    sidecarId: string,
    identification: PairingIdentification,
    command: SidecarCommandRequest
  ): Promise<(SidecarJobStatus & { success: true })["result"] | undefined> {
    const enqueue = await this.enqueueCommand(sidecarId, identification, command);
    if (!enqueue.success) {
      modalService.openModal(ErrorModal, { error: enqueue.error });
      return;
    }
    try {
      return await this.awaitJob(sidecarId, enqueue.jobId, identification);
    } catch (error: any) {
      modalService.openModal(ErrorModal, { error: error?.message ?? error });
    }
  }

  async selectCommand(sidecar: ClientSideSidecar): Promise<void> {
    const res = await modalService.openModal(SelectCommandModal).promise;
    if (!res?.command) {
      return;
    }
    if (res.command === "startSidecar") {
      await this.startSidecar(sidecar._id, res.verbose, res.resyncAllMessages);
      return;
    }
    const data = observable({});
    const modal = modalService.openModal(res.command in commandModals ? (commandModals as any)[res.command] : commandModals.default, { data } as any);
    const enqueue = await this.enqueueCommand(sidecar._id, { etoken: this.userStore.etoken ?? "", orgId: sidecar.orgId }, res);
    if (!enqueue.success) {
      modal.close(null);
      modalService.openModal(ErrorModal, { error: enqueue.error });
      return;
    }
    const { jobId } = enqueue;
    const abortController = new AbortController();
    modal.promise.finally(() => abortController.abort());
    try {
      const result = await this.awaitJob(sidecar._id, jobId, { etoken: this.userStore.etoken ?? "", orgId: sidecar.orgId }, abortController.signal);
      runInAction(() => Object.assign(data, result, { success: true }));
    } catch (error: any) {
      modal.close(null);
      if (error !== abortController.signal.reason) modalService.openModal(ErrorModal, { error: error?.message ?? error });
    }
    this.updateSidecars();
  }

  async startSidecar(sidecarId: string, verbose = false, resyncAllMessages = false) {
    const searchParams = new URLSearchParams(await this.urlSearchParams);
    if (verbose) searchParams.set("verbose", "true");
    if (resyncAllMessages) searchParams.set("resyncAllMessages", "true");
    await getJSON({
      urlSuffix: `/sidecars/${sidecarId}/start?${searchParams}`,
      etoken: this.userStore.etoken ?? "",
    }).then((res) => res.json());
    this.updateSidecars();
  }

  async clearQueryCache() {
    this.clearQueryCacheFromPromise = fromPromise(
      getJSON({
        urlSuffix: `/message/cache/clear?orgId=${this.orgStore.orgId}`,
        etoken: this.userStore.etoken ?? "",
      })
    );
  }

  async sidecarSettings(sidecar: ClientSideSidecar) {
    const metadata = sidecar.metadata ?? {
      notifyEmail: true,
      autoReinvite: true,
      periodicReinvite: true,
      locale: i18n.language,
    };
    const settings = await modalService.openModal(SidecarSettingsModal, {
      options: {
        runMode: sidecar.runMode,
        notifyEmail: metadata.notifyEmail ? "on" : "off",
        autoReinvite: metadata.autoReinvite ? "on" : "off",
        periodicReinvite: metadata.periodicReinvite ? "on" : "off",
        locale: metadata.locale ?? i18n.language,
      },
    }).promise;
    if (!settings) {
      return;
    }
    await postJSON({
      urlSuffix: `/sidecars/${sidecar._id}/settings${await this.urlSearchParams}`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({
        runMode: settings.runMode,
        autoReinvite: settings.autoReinvite === "on",
        periodicReinvite: settings.periodicReinvite === "on",
        notifyEmail: settings.notifyEmail === "on",
        locale: settings.locale,
      }),
    }).then((res) => res.json());
    this.updateSidecars();
  }

  async deleteSidecar(sidecarId: string, t: TFunction) {
    const res = await modalService.openModal(ConfirmModal, {
      title: t("sidecars.delete_sidecar"),
      body: t("sidecars.delete_sidecar_modal_message"),
    }).promise;
    if (!res?.result) {
      return;
    }
    await deleteJSON({
      urlSuffix: `/sidecars/${sidecarId}${await this.urlSearchParams}`,
      etoken: this.userStore.etoken ?? "",
      body: "{}",
    }).then((res) => res.json());
    this.updateSidecars();
  }

  async bulkReinviteSidecars(sidecarIds: string[], t: TFunction) {
    if (sidecarIds.length === 0) return;

    const response = await postJSON({
      urlSuffix: `/sidecars/bulk/re-invite${await this.urlSearchParams}`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({ sidecarIds }),
    }).then((r) => parseOrReturnError<BulkReinviteSidecars>(r, this.logger));

    if (!response.success) {
      modalService.openModal(Toast, { text: t("sidecars.bulk_reinvite.failed"), type: "error" });
      return;
    }

    modalService.openModal(Toast, { text: t("sidecars.bulk_reinvite.success", { count: sidecarIds.length }), type: "regular" });
    this.deselectAllItems();
    this.updateSidecars();
  }

  async bulkDeleteSidecars(sidecarIds: string[], t: TFunction) {
    if (sidecarIds.length === 0) return;

    const res = await modalService.openModal(ConfirmModal, {
      title: t("sidecars.bulk_delete.confirm_title"),
      body: t("sidecars.bulk_delete.confirm_message", { count: sidecarIds.length }),
    }).promise;

    if (!res?.result) return;

    runInAction(() => {
      this.bulkDeleteProgress = { current: 0, total: sidecarIds.length };
    });

    const results: Array<{ success: boolean; id: string }> = [];
    for (let i = 0; i < sidecarIds.length; i++) {
      const id = sidecarIds[i];
      try {
        const response = await deleteJSON({
          urlSuffix: `/sidecars/${id}${await this.urlSearchParams}`,
          etoken: this.userStore.etoken ?? "",
          body: "{}",
        }).then((r) => parseOrReturnError<DeleteSidecar>(r, this.logger));
        results.push({ success: response.success, id });
      } catch (error) {
        results.push({ success: false, id });
      }

      runInAction(() => {
        this.bulkDeleteProgress = { current: i + 1, total: sidecarIds.length };
      });
    }

    runInAction(() => {
      this.bulkDeleteProgress = null;
    });

    const successCount = results.filter((r) => r.success).length;
    const failCount = sidecarIds.length - successCount;

    if (failCount === 0) {
      modalService.openModal(Toast, { text: t("sidecars.bulk_delete.success", { count: successCount }), type: "regular" });
    } else if (successCount > 0) {
      modalService.openModal(Toast, { text: t("sidecars.bulk_delete.partial_success", { successCount, failCount }), type: "error" });
    } else {
      modalService.openModal(Toast, { text: t("sidecars.bulk_delete.failed"), type: "error" });
    }

    this.deselectAllItems();
    this.updateSidecars();
  }

  toggleItemSelection(sidecar: ClientSideSidecar) {
    if (this.selectedItems.find((item) => item._id === sidecar._id)) {
      this.selectedItems = this.selectedItems.filter((item) => item._id !== sidecar._id);
    } else {
      this.selectedItems.push(sidecar);
    }
  }

  deselectAllItems() {
    this.selectedItems = [];
  }

  setSearchTerm(searchTerm: string) {
    this.urlStore.setSearchParams({ search: searchTerm });
  }

  toggleStatusFilter(status: Status) {
    let newFilter = new Set(this.statusFilters);
    if (newFilter.size === Object.values(SIDECAR_STATUS).length) {
      newFilter = new Set([status]);
    } else if (newFilter.size === 1 && newFilter.has(status)) {
      newFilter = new Set(Object.values(SIDECAR_STATUS));
    } else if (newFilter.has(status)) {
      newFilter.delete(status);
    } else {
      newFilter.add(status);
    }
    this.statusFilters = newFilter;
  }
  toggleRunnerFilter(runner: (typeof SIDECAR_RUNNERS)[number]) {
    let newFilter = new Set(this.runnerFilter);
    if (newFilter.size === SIDECAR_RUNNERS.length) {
      newFilter = new Set([runner]);
    } else if (newFilter.size === 1 && newFilter.has(runner)) {
      newFilter = new Set(SIDECAR_RUNNERS);
    } else if (newFilter.has(runner)) {
      newFilter.delete(runner);
    } else {
      newFilter.add(runner);
    }
    this.runnerFilter = newFilter;
  }
  toggleGroupsFilter(group: string) {
    const newFilter = new Set(this.groupsFilter);
    if (newFilter.has(group)) {
      newFilter.delete(group);
    } else {
      newFilter.add(group);
    }
    this.groupsFilter = newFilter;
  }

  set channel(channel: SupportedSidecarChannel | undefined) {
    this._channel = channel;
  }

  get channel() {
    return this._channel;
  }
}
